import { createFileRoute, Link } from '@tanstack/react-router'
import { Activity, ClipboardList, BarChart3, Brain, FileText, ArrowRight, BookOpen, Briefcase, Layers, GraduationCap, Milestone } from 'lucide-react'
import { ProtectedRoute } from '../../components/ProtectedRoute'
import { useAuth } from '../../hooks/useAuth'
import { can } from '../../lib/roles'
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/kinerja/')({
  component: KinerjaIndexPage,
})

function KinerjaIndexPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <KinerjaIndexContent />
    </ProtectedRoute>
  )
}

const SECTIONS = [
  {
    to: '/kinerja/my-assessments/',
    label: 'My Assessments',
    description: 'View and complete your performance assessments.',
    icon: ClipboardList,
  },
  {
    to: '/kinerja/metrics/',
    label: 'Metrics',
    description: 'Track and record quantitative performance indicators.',
    icon: BarChart3,
  },
  {
    to: '/kinerja/psychometrics/',
    label: 'Psychometrics',
    description: 'Take cognitive and personality tests.',
    icon: Brain,
  },
] as const

const MANAGER_SECTIONS = [
  {
    to: '/kinerja/templates/',
    label: 'Assessment Templates',
    description: 'Create and manage assessment templates for your team.',
    icon: FileText,
  },
  {
    to: '/kinerja/competencies/',
    label: 'Competency Dictionary',
    description: 'Define competency benchmarks and 5-level behavioral indicators.',
    icon: BookOpen,
  },
  {
    to: '/kinerja/positions/',
    label: 'Positions',
    description: 'Set competency profiles for each role — the standard employees are measured against.',
    icon: Briefcase,
  },
  {
    to: '/kinerja/org-levels/',
    label: 'Org Levels',
    description: 'Define the seniority hierarchy for your company (e.g. Member, Lead, Manager).',
    icon: Layers,
  },
  {
    to: '/kinerja/training-catalogue/',
    label: 'Training Catalogue',
    description: 'Manage internal and external trainings with competency improvement mappings.',
    icon: GraduationCap,
  },
  {
    to: '/kinerja/milestone-templates/',
    label: 'Milestone Templates',
    description: 'Define standard 30/90/180-day milestones per role to guide IDP generation.',
    icon: Milestone,
  },
] as const

function KinerjaIndexContent() {
  const { user } = useAuth()
  const canManage = can(user?.role, 'manage:kinerja')

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-brand-50">
          <Activity className="w-5 h-5 text-brand-500" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Kinerja</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Performance management and assessments</p>
        </div>
      </div>

      {/* Core sections */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Sections</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECTIONS.map(({ to, label, description, icon: Icon }) => (
            <Link key={to} to={to}>
              <Card className="h-full hover:shadow-md transition-shadow group cursor-pointer border-border">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className={cn('p-2 rounded-md bg-brand-50 mb-3')}>
                      <Icon className="w-4 h-4 text-brand-500" />
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all mt-1" />
                  </div>
                  <CardTitle className="text-sm font-semibold group-hover:text-brand-600 transition-colors">
                    {label}
                  </CardTitle>
                  <CardDescription className="text-xs leading-relaxed">
                    {description}
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Manager-only sections */}
      {canManage && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Management</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MANAGER_SECTIONS.map(({ to, label, description, icon: Icon }) => (
              <Link key={to} to={to}>
                <Card className="h-full hover:shadow-md transition-shadow group cursor-pointer border-border">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="p-2 rounded-md bg-brand-50 mb-3">
                        <Icon className="w-4 h-4 text-brand-500" />
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all mt-1" />
                    </div>
                    <CardTitle className="text-sm font-semibold group-hover:text-brand-600 transition-colors">
                      {label}
                    </CardTitle>
                    <CardDescription className="text-xs leading-relaxed">
                      {description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
